import { useCallback, useMemo } from 'react';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { ButtonProps } from '@frontend/design-system';
import { PaymentMethodsProps } from '../../payment-methods';
import { PaymentMethodsView } from '../types/provider.types';
import { useDeletePaymentMethod } from './use-delete-payment-method';
import { usePaymentMethodsDerivedState } from './use-payment-methods-derived-state';
import { PaymentMethodsJustifiApi } from './use-payment-methods-justifi-api';

export type UsePaymentMethodsJustifiProps = Omit<PaymentMethodsProps, 'children'> & {
  api: PaymentMethodsJustifiApi;
  locationId: string | undefined;
  selectedPaymentMethod: PaymentMethod | null;
  onSelectPaymentMethod: (paymentMethod: PaymentMethod | null) => void;
  onChangeView: (view: PaymentMethodsView) => void;
};

export const usePaymentMethodsJustifi = ({
  onError,
  onPaymentMethodSelectedSuccess, // final success callback
  onSelectPaymentMethod, // set selected
  onCancel,
  onChangeView,
  selectedPaymentMethod,
  showCancelButtonOnList,
  api,
  personId,
  person: propsPerson,
  locationId,
  invoiceId,
  amount,
  frequency,
}: UsePaymentMethodsJustifiProps) => {
  const { person, paymentMethods, defaultPaymentMethod, isLoadingPaymentMethods, refetchPaymentMethodsAsync } =
    usePaymentMethodsDerivedState({
      personId,
      person: propsPerson,
      locationId,
      useGetPaymentMethodsByPerson: api.useGetPaymentMethodsByPerson,
      useQueryLegacyPerson: api.useQueryLegacyPerson,
    });

  const listPrimary = useMemo<ButtonProps>(() => {
    return {
      label: 'Save',
      variant: 'primary',
      disabled: !selectedPaymentMethod,
      onClick: () => {
        if (!selectedPaymentMethod) {
          onError?.('No payment method selected');
          return;
        }
        onPaymentMethodSelectedSuccess(selectedPaymentMethod);
      },
    };
  }, [selectedPaymentMethod, onError, onPaymentMethodSelectedSuccess]);

  const listSecondary = useMemo<ButtonProps | undefined>(() => {
    if (!showCancelButtonOnList) {
      return undefined;
    }
    return {
      label: 'Cancel',
      variant: 'secondary',
      onClick: () => {
        onCancel?.();
      },
    };
  }, [onCancel, showCancelButtonOnList]);

  const shouldShowFooter = !!person?.personId;

  const shouldShowLoading = !person?.personId || isLoadingPaymentMethods;

  const refetchPaymentMethods = useCallback(async () => {
    await refetchPaymentMethodsAsync();
  }, [refetchPaymentMethodsAsync]);

  const { isDeleting, ConFirmDeleteModal, showConfirmDeletePaymentModal, resetPaymentMethodToDelete } =
    useDeletePaymentMethod({
      locationId,
      personId: person?.personId,
      api,
    });

  const onClickAddCard = useCallback(() => {
    onChangeView('add-card');
  }, [onChangeView]);

  const onClickAddAch = useCallback(() => {
    console.warn('Add ACH not implemented');
  }, []);

  const value = {
    refetchPaymentMethods,
    defaultPaymentMethod,
    listPrimary,
    listSecondary,
    shouldShowFooter,
    shouldShowLoading,
    person,
    paymentMethods: paymentMethods || [],
    isDeleting,
    ConFirmDeleteModal,
    invoiceId,
    showCancelButtonOnList,
    amount,
    frequency,
    onError,
    onCancel,
    onClickAddCard,
    onClickAddAch,
    showConfirmDeletePaymentModal,
    resetPaymentMethodToDelete,
    onSelectPaymentMethod,
    onPaymentMethodSelectedSuccess,
  };

  return value;
};

export type UsePaymentMethodsJustifi = ReturnType<typeof usePaymentMethodsJustifi>;
